"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { sendForgotPasswordOtp, verifyForgotPasswordOtp, resetPassword } from "../api/forgotPassword";
import { showError } from "@/lib/error-toast";

export type ForgotPasswordStep = "identifier" | "otp" | "password" | "success";

const TOAST_ID = "forgot-password";

export function useForgotPassword() {
  const router = useRouter();
  const [step, setStep] = useState<ForgotPasswordStep>("identifier");
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  function getPayload() {
    const trimmed = identifier.trim();
    if (trimmed.includes("@")) {
      return { email: trimmed };
    }
    return { phone: trimmed };
  }

  async function handleSendOtp(event?: React.FormEvent) {
    if (event) event.preventDefault();
    if (!identifier.trim()) {
      showError(new Error("Please enter your email or phone number"));
      return;
    }

    setIsLoading(true);
    toast.loading("Sending code...", { id: TOAST_ID });

    try {
      await sendForgotPasswordOtp(getPayload());
      setStep("otp");
      toast.success("Verification code sent successfully", { id: TOAST_ID });
    } catch (error: any) {
      showError(error, "Failed to send verification code", { id: TOAST_ID });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerifyOtp(event: React.FormEvent) {
    event.preventDefault();
    if (!otp.trim()) {
      showError(new Error("Please enter the verification code"));
      return;
    }

    setIsLoading(true);
    toast.loading("Verifying code...", { id: TOAST_ID });

    try {
      const payload = getPayload();
      const result = await verifyForgotPasswordOtp({ ...payload, otp: otp.trim() });
      setSetupToken(result.token);
      setStep("password");
      toast.success("Code verified successfully. Enter your new password.", { id: TOAST_ID });
    } catch (error: any) {
      showError(error, "Invalid or expired code", { id: TOAST_ID });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResetPassword(event: React.FormEvent) {
    event.preventDefault();
    if (password.length < 8) {
      showError(new Error("Password must be at least 8 characters long"));
      return;
    }

    if (password !== confirmPassword) {
      showError(new Error("Passwords do not match"));
      return;
    }

    setIsLoading(true);
    toast.loading("Resetting password...", { id: TOAST_ID });

    try {
      const payload = getPayload();
      await resetPassword({
        ...payload,
        password,
        token: setupToken,
      });

      setStep("success");
      toast.success("Password reset successfully!", { id: TOAST_ID });

      setTimeout(() => {
        router.replace("/login");
        router.refresh();
      }, 2000);
    } catch (error: any) {
      showError(error, "Failed to reset password", { id: TOAST_ID });
    } finally {
      setIsLoading(false);
    }
  }

  function handleBackToIdentifier() {
    setStep("identifier");
    setOtp("");
  }

  return {
    step,
    identifier,
    otp,
    password,
    confirmPassword,
    isLoading,
    setIdentifier,
    setOtp,
    setPassword,
    setConfirmPassword,
    handleSendOtp,
    handleVerifyOtp,
    handleResetPassword,
    handleBackToIdentifier,
  };
}
